import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchProjectById } from '../utils/projects';
import ProjectDescription from './ProjectDescription';
import ProgressiveImage from './ProgressiveImage';
import RichMarkdown from './RichMarkdown';
import Header from './Header';

/**
 * Project - Exact copy of TwoColumnLayout with project data
 */

const SCROLL_THRESHOLD = 5;
const VISIBILITY_CHANGE_COOLDOWN = 400; // ms to ignore scroll events after header visibility changes

function Project({ onCVClick, cvVisible, setCVVisible }) {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [headerVisible, setHeaderVisible] = useState(true);
    
    // Track scroll per column
    const scrollMapRef = useRef(new Map());
    // Left/right column refs so we can reset scroll when project changes
    const leftColumnRef = useRef(null);
    const rightColumnRef = useRef(null);
    // Cooldown ref to prevent scroll handling right after visibility change
    const cooldownRef = useRef(false);

    const handleScroll = useCallback((e) => {
        const target = e.target;
        
        // Skip during cooldown (after header visibility change)
        if (cooldownRef.current) return;
        
        // Only handle scroll if content is actually scrollable
        const isScrollable = target.scrollHeight > target.clientHeight;
        if (!isScrollable) return;
        
        const currentScroll = target.scrollTop;
        const lastScroll = scrollMapRef.current.get(target) || 0;
        const delta = currentScroll - lastScroll;

        if (Math.abs(delta) > SCROLL_THRESHOLD) {
            if (delta > 0 && currentScroll > 50) {
                // Scrolling down - hide header
                setHeaderVisible(prev => {
                    if (prev === true) {
                        // Starting cooldown
                        cooldownRef.current = true;
                        setTimeout(() => { cooldownRef.current = false; }, VISIBILITY_CHANGE_COOLDOWN);
                    }
                    return false;
                });
            } else if (delta < 0) {
                // Scrolling up - show header
                setHeaderVisible(prev => {
                    if (prev === false) {
                        // Starting cooldown
                        cooldownRef.current = true;
                        setTimeout(() => { cooldownRef.current = false; }, VISIBILITY_CHANGE_COOLDOWN);
                    }
                    return true;
                });
            }
            scrollMapRef.current.set(target, currentScroll);
        }
    }, []);

    useEffect(() => {
        async function loadProject() {
            setLoading(true);
            setProject(null);
            setError(null);

            const id = parseInt(projectId, 10);
            if (!id) {
                setError('Invalid project id');
                setLoading(false);
                return;
            }
            const data = await fetchProjectById(id);
            if (!data) {
                setError('Project not found or error occurred');
                setLoading(false);
                return;
            }
            const safeParse = (val, fallback) => {
                if (val == null) return fallback;
                if (Array.isArray(val)) return val;
                if (typeof val === 'string') {
                    const trimmed = val.trim();
                    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                        try {
                            return JSON.parse(trimmed);
                        } catch (e) {
                            return fallback;
                        }
                    }
                    return fallback;
                }
                return fallback;
            };

            const content = safeParse(data.content, [[], []]);
            setProject({
                title: data.project_title,
                date: `${data.date_since} – ${data.date_to}`,
                category: data.category,
                description: data.description,
                url: data.url,
                label: data.project_title,
                name: data.project_title,
                content: content,
            });
            setLoading(false);
        }
        loadProject();
    }, [projectId]);

    useEffect(() => {
        if (!loading) {
            if (leftColumnRef.current) {
                leftColumnRef.current.scrollTop = 0;
            }
            if (rightColumnRef.current) {
                rightColumnRef.current.scrollTop = 0;
            }
            scrollMapRef.current.clear();
        }
    }, [projectId, loading]);

    // Loading/error states - use same wrapper style
    if (loading) {
        return (
            <div style={styles.wrapper}>
                <Header onCVClick={onCVClick} cvVisible={cvVisible} setCVVisible={setCVVisible} headerVisible={true} />
                <div style={{ padding: 20 }}>Loading...</div>
            </div>
        );
    }
    if (error) {
        return (
            <div style={styles.wrapper}>
                <Header onCVClick={onCVClick} cvVisible={cvVisible} setCVVisible={setCVVisible} headerVisible={true} />
                <div style={{ padding: 20 }}>Error: {error}</div>
            </div>
        );
    }
    if (!project) {
        return (
            <div style={styles.wrapper}>
                <Header onCVClick={onCVClick} cvVisible={cvVisible} setCVVisible={setCVVisible} headerVisible={true} />
                <div style={{ padding: 20 }}>Project not found.</div>
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            {/* Use actual Header component - pass headerVisible to control it */}
            <Header 
                onCVClick={onCVClick} 
                cvVisible={cvVisible} 
                setCVVisible={setCVVisible} 
                headerVisible={headerVisible}
            />

            {/* Two Column Container */}
            <div style={styles.columnsContainer}>
                {/* Left Column */}
                <div 
                    ref={leftColumnRef}
                    className="project-side"
                    style={{
                        ...styles.column,
                        ...styles.leftColumn,
                        paddingTop: headerVisible ? 'var(--header-height, 80px)' : 0,
                        transition: 'padding-top 0.3s ease',
                    }}
                    onScroll={handleScroll}
                >
                    <div style={styles.leftColumnContent}>
                        <ProjectDescription project={project} />
                        {Array.isArray(project.content) && Array.isArray(project.content[0]) && project.content[0].map((block, idx) => (
                            <div key={`left-${idx}`} style={{ marginBottom: 12 }}>
                                {block.type === 'text' && <RichMarkdown source={block.value || ''} />}
                                {block.type === 'image' && (
                                    <div>
                                        <ProgressiveImage
                                            src={block.value}
                                            alt={`left-${idx}`}
                                            style={{ width: '100%', minHeight: 180 }}
                                        />
                                        {block.description && (
                                            <div style={{ fontFamily: 'Lora, serif', fontSize: '12px', marginTop: 6, color: '#000' }}>
                                                {block.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {block.type === 'video' && <div><a href={block.value} target="_blank" rel="noreferrer">{block.value}</a></div>}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Column */}
                <div 
                    ref={rightColumnRef}
                    className="project-side"
                    style={{
                        ...styles.column,
                        ...styles.rightColumn,
                        paddingTop: headerVisible ? 'var(--header-height, 80px)' : 0,
                        transition: 'padding-top 0.3s ease',
                    }}
                    onScroll={handleScroll}
                >
                    <div style={styles.rightColumnContent}>
                        {Array.isArray(project.content) && Array.isArray(project.content[1]) && project.content[1].map((block, idx) => (
                            <div key={`right-${idx}`} style={{ marginBottom: 12 }}>
                                {block.type === 'text' && <RichMarkdown source={block.value || ''} />}
                                {block.type === 'image' && (
                                    <div>
                                        <ProgressiveImage
                                            src={block.value}
                                            alt={`right-${idx}`}
                                            style={{ width: '100%', minHeight: 180 }}
                                        />
                                        {block.description && (
                                            <div style={{ fontFamily: 'Lora, serif', fontSize: '12px', marginTop: 0, color: '#000' }}>
                                                {block.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {block.type === 'video' && <div><a href={block.value} target="_blank" rel="noreferrer">{block.value}</a></div>}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// EXACT same styles as TwoColumnLayout
const styles = {
    wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
    },
    columnsContainer: {
        display: 'flex',
        width: '100%',
        height: '100%',
    },
    column: {
        flex: 1,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
    },
    leftColumn: {
        background: '#ffffff',
    },
    rightColumn: {
        background: '#ffffff',
    },
    columnContent: {
        padding: 8,
    },
    leftColumnContent: {
        padding: 8,
    },
    rightColumnContent: {
        padding: 0,
    },
};

export default Project;
